import React, { useState } from 'react';
import { PlayIcon, ScrollDownIcon, CloseIcon } from './Icons.tsx';
import CardSwap, { Card } from './CardSwap.tsx';
import ContactModal from './ContactModal.tsx';

const cardContent = [
  {
    title: 'Real-Time Equipment Dashboard',
    description: 'Monitor every item\'s status, inspection validity, and assignment history with real-time analytics, metrics, and alerts.',
    imgSrc: 'https://ventro.energy/dashboardeq.jpg',
    alt: 'Shield platform dashboard showing equipment analytics and overview.'
  },
  {
    title: 'Digital Inspection Management',
    description: 'Track inspection validity with color-coded alerts, perform digital checks, and instantly generate PDF compliance certificates.',
    imgSrc: 'https://ventro.energy/inspection.jpg',
    alt: 'Digital inspection checklist on a tablet.'
  },
  {
    title: 'Assignment Management',
    description: 'Easily assign equipment to users or projects. Track assignments, manage returns, and maintain a clear chain of custody.',
    imgSrc: 'https://ventro.energy/assigment.jpg',
    alt: 'Equipment assignment tracking interface showing assignment history and management.'
  },
  {
    title: 'Mobile Access & QR Codes',
    description: 'Equip your team with our mobile app. Scan QR codes to instantly access equipment details and perform actions in the field.',
    imgSrc: 'https://ventro.energy/mobline.jpg',
    alt: 'Mobile app showing QR code scanning and equipment access.'
  },
];

const VideoModal = ({ isVisible, onClose }: { isVisible: boolean; onClose: () => void }) => {
  if (!isVisible) return null;

  return (
    <div className={`video-modal-overlay ${isVisible ? 'visible' : ''}`} onClick={onClose}>
      <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="video-modal-close">
          <CloseIcon className="h-8 w-8" />
        </button>
        <div className="video-aspect-ratio bg-gray-100/70 dark:bg-white/5 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden">
          <iframe
            src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1&rel=0" // Placeholder video
            title="Shield Product Demo"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </div>
    </div>
  );
};


const Hero: React.FC = () => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [isContactModalVisible, setContactModalVisible] = useState(false);

  return (
    <>
      <section className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden bg-white dark:bg-transparent">
        <div className="absolute inset-0 bg-grid-black/[0.08] dark:bg-grid-white/[0.05] [mask-image:linear-gradient(to_bottom,white_5%,transparent_90%)]"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            
            {/* Left Column: Text Content */}
            <div className="text-center md:text-left">
              <div className="inline-block bg-orange-600 dark:bg-orange-500/10 text-white dark:text-[#FF4D00] text-sm font-medium px-4 py-1.5 rounded-full mb-4 dark:border dark:border-orange-500/20">
                Complete Audit Trail & 100% Compliance
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white leading-tight tracking-tighter mb-6 [text-shadow:0_2px_10px_rgba(0,0,0,0.12)] dark:text-shadow-none">
                The Command Center for <span className="text-orange-600 dark:text-[#FF4D00]">Your Equipment</span> Operations
              </h1>
              <p className="text-lg md:text-xl text-gray-700 dark:text-gray-400 max-w-xl mx-auto md:mx-0 mb-10 [text-shadow:0_1px_5px_rgba(0,0,0,0.1)] dark:text-shadow-none">
                Shield provides a comprehensive suite of tools to manage your entire equipment inventory, streamline inspections, and ensure safety compliance, reducing administrative time by up to 60%.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4">
                <button
                  onClick={() => setContactModalVisible(true)}
                  className="w-full sm:w-auto bg-orange-600 text-white dark:bg-[#FF4D00] dark:text-black font-semibold px-8 py-4 rounded-lg hover:bg-orange-700 dark:hover:bg-white transition-all duration-300 transform hover:scale-105 flex items-center justify-center shadow-2xl shadow-orange-600/20 dark:shadow-2xl dark:shadow-[#FF4D00]/20"
                >
                  Get Started Free
                </button>
                <button
                  onClick={() => setModalVisible(true)}
                  className="w-full sm:w-auto bg-white dark:bg-white/10 text-gray-800 dark:text-white font-semibold px-8 py-4 rounded-lg hover:bg-gray-100 dark:hover:bg-white/20 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 border border-gray-300 dark:border-white/20 shadow-lg shadow-orange-600/20 dark:shadow-2xl dark:shadow-[#FF4D00]/20"
                >
                  <PlayIcon className="h-5 w-5" />
                  Watch Demo
                </button>
              </div>
            </div>

            {/* Right Column: Visual */}
            <div className="relative flex items-center justify-center order-first md:order-last min-h-[600px]">
              <CardSwap pauseOnHover width={620} height={520} cardDistance={80} verticalDistance={92}>
                {cardContent.map((card, index) => (
                  <Card key={index} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 flex flex-col overflow-hidden shadow-2xl shadow-orange-600/20 dark:shadow-2xl dark:shadow-[#FF4D00]/20">
                    {/* Browser Mockup Header */}
                    <div className="flex-shrink-0 bg-gray-900">
                      <div className="h-8 flex items-center px-3 gap-1.5 border-b border-gray-700">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                        <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                        <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
                      </div>
                      <div className="px-2.5 py-0.5 flex items-center border-b border-gray-700">
                        <div className="bg-gray-800 rounded px-2 py-0.5 text-[10px] text-gray-400 flex items-center gap-1 w-full max-w-[90%]">
                          <svg className="w-2.5 h-2.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span className="truncate">https://ventro.energy</span>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-grow flex flex-col">
                      <img src={card.imgSrc} alt={card.alt} className="w-full object-cover flex-shrink-0 h-[320px]" />
                      <div className="text-left flex flex-col p-3 bg-white dark:bg-gray-900 border-t-2 border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">{card.title}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{card.description}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </CardSwap>
            </div>
          </div>
        </div>
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2">
          <ScrollDownIcon className="w-8 h-8 text-gray-500 dark:text-gray-600 animate-bounce-subtle" />
        </div>
        <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-white dark:from-[#0A0A0A] to-transparent"></div>
      </section>
      <VideoModal isVisible={isModalVisible} onClose={() => setModalVisible(false)} />
      <ContactModal isVisible={isContactModalVisible} onClose={() => setContactModalVisible(false)} />
    </>
  );
};

export default Hero;